import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { CalendarDaysIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useMemo } from 'react';
import { useFormatDate } from '../../hooks/useFormatDate';
import { T, useT } from '../../translation/src';
import { DayInput } from '../formik/FormDayInput';

export type DateRangePreset = 'all' | 'thisMonth' | 'lastMonth' | 'nextMonth' | 'thisQuarter' | 'lastQuarter' | 'nextQuarter' | 'thisYear' | 'lastYear' | 'last30Days' | 'next30Days' | 'next90Days' | 'custom';

/** A range of days, UTC: `from` included, `to` included; either open. As yyyy-mm-dd. */
export interface DateRange {
  preset: DateRangePreset;
  from?: string;
  to?: string;
}

export const ALL_TIME: DateRange = { preset: 'all' };

const day = (date: Date) => date.toISOString().slice(0, 10);
const utc = (year: number, month: number, date: number) => new Date(Date.UTC(year, month, date));

// The days a preset covers, counted from today (UTC, like the invoices' months).
export const rangeOf = (preset: DateRangePreset, now = new Date()): Pick<DateRange, 'from' | 'to'> => {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const quarter = Math.floor(month / 3) * 3;
  switch (preset) {
    case 'thisMonth':
      return { from: day(utc(year, month, 1)), to: day(utc(year, month + 1, 0)) };
    case 'lastMonth':
      return { from: day(utc(year, month - 1, 1)), to: day(utc(year, month, 0)) };
    case 'thisQuarter':
      return { from: day(utc(year, quarter, 1)), to: day(utc(year, quarter + 3, 0)) };
    case 'lastQuarter':
      return { from: day(utc(year, quarter - 3, 1)), to: day(utc(year, quarter, 0)) };
    case 'thisYear':
      return { from: day(utc(year, 0, 1)), to: day(utc(year, 11, 31)) };
    case 'lastYear':
      return { from: day(utc(year - 1, 0, 1)), to: day(utc(year - 1, 11, 31)) };
    case 'last30Days':
      return { from: day(utc(year, month, now.getUTCDate() - 29)), to: day(now) };
    case 'nextMonth':
      return { from: day(utc(year, month + 1, 1)), to: day(utc(year, month + 2, 0)) };
    case 'nextQuarter':
      return { from: day(utc(year, quarter + 3, 1)), to: day(utc(year, quarter + 6, 0)) };
    case 'next30Days':
      return { from: day(now), to: day(utc(year, month, now.getUTCDate() + 29)) };
    case 'next90Days':
      return { from: day(now), to: day(utc(year, month, now.getUTCDate() + 89)) };
    default:
      return {};
  }
};

// Whether a moment falls in the range (whole UTC days).
export const inRange = (at: string | number | Date, range: DateRange): boolean => {
  const date = day(new Date(at));
  return (!range.from || date >= range.from) && (!range.to || date <= range.to);
};

// Looking back, for what happened (sales): the default.
export const PAST_PRESETS: DateRangePreset[] = ['all', 'thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear', 'last30Days'];
// Looking ahead, for what is coming (renewals).
export const UPCOMING_PRESETS: DateRangePreset[] = ['all', 'next30Days', 'next90Days', 'thisMonth', 'nextMonth', 'thisQuarter', 'nextQuarter', 'thisYear'];

const usePresetLabels = (): Record<DateRangePreset, string> => {
  const all = useT('all time', { swc: true });
  const thisMonth = useT('this month', { swc: true });
  const lastMonth = useT('last month', { swc: true });
  const nextMonth = useT('next month', { swc: true });
  const thisQuarter = useT('this quarter', { swc: true });
  const lastQuarter = useT('last quarter', { swc: true });
  const nextQuarter = useT('next quarter', { swc: true });
  const thisYear = useT('this year', { swc: true });
  const lastYear = useT('last year', { swc: true });
  const last30Days = useT('last 30 days', { swc: true });
  const next30Days = useT('next 30 days', { swc: true });
  const next90Days = useT('next 90 days', { swc: true });
  const custom = useT('custom', { swc: true });
  return useMemo(
    () => ({ all, thisMonth, lastMonth, nextMonth, thisQuarter, lastQuarter, nextQuarter, thisYear, lastYear, last30Days, next30Days, next90Days, custom }),
    [all, thisMonth, lastMonth, nextMonth, thisQuarter, lastQuarter, nextQuarter, thisYear, lastYear, last30Days, next30Days, next90Days, custom],
  );
};

interface DateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  // The presets offered, in order (PAST_PRESETS when left out).
  presets?: DateRangePreset[];
}
// A button showing the period, opening the presets (by default this / last month, quarter, year, the last 30 days)
// and two day fields for a custom range. Everything is in whole UTC days.
const DateRangePicker_: React.FC<DateRangePickerProps> = ({ value, onChange, presets = PAST_PRESETS }) => {
  const labels = usePresetLabels();
  const { formatDay } = useFormatDate();
  const fromLabel = useT('from', { swc: true });
  const toLabel = useT('to', { swc: true });
  const shown = value.preset === 'all' ? labels.all : `${labels[value.preset]}${value.from || value.to ? `: ${value.from ? formatDay(value.from) : '…'} – ${value.to ? formatDay(value.to) : '…'}` : ''}`;

  return (
    <Popover className="relative">
      <PopoverButton className="flex h-10 items-center gap-2 rounded-md border border-[#C2C6C8] bg-white px-3 text-sm outline-none transition hover:border-black dark:border-gray-300 dark:bg-[#313131] dark:hover:border-neon-green-300">
        <CalendarDaysIcon className="h-4 w-4" />
        <span className="whitespace-nowrap">{shown}</span>
        <ChevronDownIcon className="h-4 w-4" />
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom start"
        className="z-50 flex w-72 flex-col gap-3 rounded-xl border border-gray-300 bg-white p-4 shadow-lg transition duration-200 ease-in-out [--anchor-gap:8px] data-[closed]:-translate-y-1 data-[closed]:opacity-0 dark:border-gray-500 dark:bg-[#313131] dark:text-white"
      >
        {({ close }) => (
          <>
            <div className="grid grid-cols-2 gap-1.5">
              {presets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    onChange({ preset, ...rangeOf(preset) });
                    close();
                  }}
                  className={classNames(
                    'rounded-md border px-2 py-1.5 text-left text-sm transition',
                    value.preset === preset ? 'border-black bg-neon-green-300 font-semibold text-eerie-black dark:border-white' : 'border-gray-300 hover:border-black dark:border-gray-600 dark:hover:border-white',
                  )}
                >
                  {labels[preset]}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-2 border-t border-gray-200 pt-3 dark:border-gray-600">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                <T _str="custom" swc />
              </span>
              <div className="flex items-center justify-between gap-2 text-sm">
                {fromLabel}
                <DayInput className="w-40" aria-label={fromLabel} value={value.from ?? ''} max={value.to} onChange={(from) => onChange({ preset: 'custom', from: from || undefined, to: value.to })} />
              </div>
              <div className="flex items-center justify-between gap-2 text-sm">
                {toLabel}
                <DayInput className="w-40" aria-label={toLabel} value={value.to ?? ''} min={value.from} onChange={(to) => onChange({ preset: 'custom', from: value.from, to: to || undefined })} />
              </div>
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
};
export const DateRangePicker = memo(DateRangePicker_);
